import BreakpointEnum from '@models/enums/BreakpointEnum';
import useBreakpoint from '@utils/hooks/useBreakpoint';
import { useEffect, useRef } from 'react';
import Swiper from 'swiper';

interface Props {
  readonly swiperInstance?: Swiper | null;
  readonly shouldAnimateCondition: boolean;
}

const breakpointAdjustments = {
  [BreakpointEnum.Small]: -1.5,
  [BreakpointEnum.Medium]: -1,
  [BreakpointEnum.Large]: -0.5,
  [BreakpointEnum.XLarge]: 0,
};

const useCarouselScrollAnimation = ({
  swiperInstance,
  shouldAnimateCondition,
}: Props) => {
  const lastScrollPosition = useRef(0);
  const lastSwiperProgress = useRef(0);
  const breakPoint = useBreakpoint();

  useEffect(() => {
    if (!swiperInstance || !shouldAnimateCondition) return () => ({});

    const updateProgressFromSwiper = () => {
      lastSwiperProgress.current = swiperInstance.progress;
    };

    swiperInstance.on('slideChangeTransitionStart', updateProgressFromSwiper);

    const handleScroll = () => {
      if (!breakPoint) return;

      const slideCount = swiperInstance.slides.length;
      const calculatedVariableRatio = Math.max(
        0.75,
        Math.min(100, 100 / slideCount) / 3 + breakpointAdjustments[breakPoint]
      );

      const carouselRect = swiperInstance.el.getBoundingClientRect();
      const carouselTopOffset = carouselRect.top - window.innerHeight;
      const isCarouselInView = window.scrollY > carouselTopOffset;

      if (isCarouselInView) {
        if (lastScrollPosition.current === 0) {
          lastScrollPosition.current = window.scrollY;
        }

        const scrollChange = window.scrollY - lastScrollPosition.current;
        const scrollChangePercentage =
          scrollChange /
          (window.innerHeight * (slideCount / calculatedVariableRatio));

        const newSwiperProgress =
          lastSwiperProgress.current + scrollChangePercentage;

        swiperInstance.setProgress(newSwiperProgress, 150);
        lastScrollPosition.current = window.scrollY;
        lastSwiperProgress.current = newSwiperProgress;
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      swiperInstance.off(
        'slideChangeTransitionStart',
        updateProgressFromSwiper
      );
    };
  }, [swiperInstance, shouldAnimateCondition, breakPoint]);
};

export default useCarouselScrollAnimation;
